import { cn } from "@/lib/utils";
import { CardShell } from "@/components/terminal/cards/CardShell";
import {
  ArrowSquareOut,
  Check,
  CheckCircle,
  Code,
  File,
  Flag,
  GitBranch,
  Lightning,
  ListBullets,
  PencilSimple,
  Robot,
  Terminal,
  Warning,
  WarningCircle,
} from "@/components/ui/icons";
import type {
  SyncEventPayload,
  SyncEventSource,
} from "@ano/shared/sync/sync-event";
import {
  EVENT_TYPE_LABELS,
  statusPillClass,
  statusTextClass,
} from "./sync-card-meta";
import {
  LinksSection,
  StringListSection,
  TestsSection,
} from "./sync-event-sections";

// Icon components share one Solar wrapper type; derive it from a real icon.
type IconType = typeof Lightning;

// Top-level static map (static-components rule: never pick a component via a
// function call in render).
const SOURCE_ICONS: Record<SyncEventSource, IconType> = {
  claude_code: Robot,
  codex: Robot,
  cursor: Code,
  github: GitBranch,
  linear: Flag,
  terminal: Terminal,
  manual: PencilSimple,
  system: Lightning,
};

/**
 * The Ano Sync timeline card. Renders a `kind="text"` message whose payload
 * carries an `ano_sync` event (see message-cards.tsx render branch). Compact
 * by default; structured sections are collapsible and omitted when empty.
 */
export function SyncEventCard({
  event,
  compact = true,
}: {
  event: SyncEventPayload;
  compact?: boolean;
}) {
  const Icon = SOURCE_ICONS[event.source] ?? Lightning;
  const statusText = statusTextClass(event.status);

  return (
    <CardShell
      compact={compact}
      telemetryCardType="sync_event"
      labelUppercase={false}
      icon={<Icon size={13} className={statusText} />}
      label={`Ano Sync · ${event.actorName}`}
      headerActions={
        <span
          className={cn(
            "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium",
            statusPillClass(event.status),
          )}
        >
          {EVENT_TYPE_LABELS[event.eventType]}
        </span>
      }
      contentClassName="px-2.5 py-2 space-y-2"
    >
      <div className="space-y-1">
        <div className="text-[13px] font-semibold text-foreground">
          {event.title}
        </div>
        {event.summary ? (
          <div className="whitespace-pre-wrap text-[12px] text-foreground/80">
            {event.summary}
          </div>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <StringListSection
          title="Changed files"
          icon={<File size={11} className="text-muted-foreground" />}
          items={event.changedFiles}
          collapsible
          defaultOpen={
            event.eventType === "completed" || event.eventType === "progress"
          }
          mono
        />
        <StringListSection
          title="Commands"
          icon={<Terminal size={11} className="text-muted-foreground" />}
          items={event.commandsRun}
          collapsible
          mono
          prefix="$ "
        />
        <TestsSection
          tests={event.tests}
          icon={<CheckCircle size={11} className="text-muted-foreground" />}
          collapsible
          defaultOpen={event.eventType === "tests"}
        />
        <StringListSection
          title="Decisions"
          icon={<Check size={11} className="text-muted-foreground" />}
          items={event.decisions}
          collapsible
          defaultOpen={event.eventType === "decision"}
          rowIcon={<Check size={12} className="text-info" />}
        />
        <StringListSection
          title="Risks"
          icon={<Warning size={11} className="text-warning" />}
          items={event.risks}
          collapsible
          rowIcon={<Warning size={12} className="text-warning" />}
        />
        <StringListSection
          title="Blockers"
          icon={<WarningCircle size={11} className="text-destructive" />}
          items={event.blockers}
          collapsible
          defaultOpen={event.eventType === "blocker"}
          rowIcon={<WarningCircle size={12} className="text-destructive" />}
        />
        <StringListSection
          title="Next steps"
          icon={<ListBullets size={11} className="text-muted-foreground" />}
          items={event.nextSteps}
          collapsible
          defaultOpen={event.eventType === "review_requested"}
          rowIcon={<ListBullets size={12} className="text-muted-foreground" />}
        />
        <LinksSection
          links={event.links}
          icon={<ArrowSquareOut size={11} className="text-muted-foreground" />}
          collapsible
        />
      </div>
    </CardShell>
  );
}
